import { Controller } from "@hotwired/stimulus";
import {
    browserLocationChangedEventName,
    browserLocationFromEvent,
    readRecentBrowserLocation,
    type SmartGuideBrowserLocation
} from "../../../Shared/BrowserLocation/SmartGuideBrowserLocation";
import { smartGuideLocationBroker } from "../../../Shared/BrowserLocation/SmartGuideLocationBroker";
import { dispatchDestinationEntryChanged } from "../../../Shared/BrowserLocation/SmartGuideDestinationEntry";

export default class DestinationExploreController extends Controller {
    private latestGpsLocation?: SmartGuideBrowserLocation;

    private readonly onBrowserLocationChanged = (event: Event): void => {
        const location = browserLocationFromEvent(event);
        if (location) {
            this.activateLocationFrames(location);
        }
    };

    connect(): void {
        document.addEventListener(browserLocationChangedEventName, this.onBrowserLocationChanged);
        this.element.addEventListener("turbo:frame-load", this.onTurboFrameLoad);
        this.element.addEventListener("turbo:frame-missing", this.onExperiencesFrameFailure);
        this.element.addEventListener("turbo:fetch-request-error", this.onExperiencesFrameFailure);
        const recentLocation = readRecentBrowserLocation({ sources: ["gps"] });
        if (recentLocation) {
            this.activateLocationFrames(recentLocation);
            return;
        }

        void smartGuideLocationBroker.requestFix("destination-explore", { profile: "balanced" })
            .then(location => {
                this.activateLocationFrames(location);
            })
            .catch(() => { /* GPS-dependent rails remain absent after denial. */ });
    }

    disconnect(): void {
        document.removeEventListener(browserLocationChangedEventName, this.onBrowserLocationChanged);
        this.element.removeEventListener("turbo:frame-load", this.onTurboFrameLoad);
        this.element.removeEventListener("turbo:frame-missing", this.onExperiencesFrameFailure);
        this.element.removeEventListener("turbo:fetch-request-error", this.onExperiencesFrameFailure);
        this.latestGpsLocation = undefined;
    }

    private readonly onExperiencesFrameFailure = (event: Event): void => {
        const frame = event.target;
        if (!(frame instanceof HTMLElement) || !frame.matches("turbo-frame[data-destination-explore-experiences-frame]")) {
            return;
        }

        event.preventDefault();
        frame.replaceChildren();
    };

    private readonly onTurboFrameLoad = (event: Event): void => {
        if (!this.latestGpsLocation || !(event.target instanceof Element)) {
            return;
        }

        const marker = event.target.querySelector<HTMLElement>("[data-destination-explore-entry-state]");
        const state = marker?.dataset.destinationExploreEntryState;
        if (state !== "inside" && state !== "outside") {
            return;
        }

        dispatchDestinationEntryChanged({
            inside: state === "inside",
            location: this.latestGpsLocation
        });
    };

    private activateLocationFrames(location: SmartGuideBrowserLocation): void {
        if (location.source !== "gps") {
            return;
        }
        this.latestGpsLocation = location;

        for (const frame of this.element.querySelectorAll<HTMLElement>("[data-destination-explore-location-url]")) {
            if (frame.getAttribute("src")) {
                continue;
            }

            const baseUrl = frame.dataset.destinationExploreLocationUrl;
            if (!baseUrl) {
                continue;
            }

            const url = new URL(baseUrl, window.location.origin);
            url.searchParams.set("latitude", location.latitude.toString());
            url.searchParams.set("longitude", location.longitude.toString());
            frame.setAttribute("src", `${url.pathname}${url.search}`);
        }
    }
}
